import { FC } from "react";
import { Outlet, useMatch } from "react-router";
import { Info } from "lucide-react";
import { FeaturePermissions } from "@vviinn/user-managment-client-ts";

import { SMALL_ICON_SIZE_STYLES } from "../ui/styles";
import { typeClassNames } from "../ui/layout/molecules/Badge/Badge";
import {
  isGlobalRole,
  isPermitted,
} from "../helpers/permissions/featurePermission";
import { useAppSelector } from "../store";
import { Skeleton } from "./DataDisplay/Skeleton";

// companyViewer is the only role denied "view" on company:members (per the bff
// permission matrix) — every other role (admin, global*, companyOwner, companyEditor)
// has at least VIEW there, so this reliably singles out the restricted viewer role
// without a dedicated "current user's role" field, which doesn't exist client-side.
const COMPANY_VIEWER_PROXY_PERMISSION = "company:members:view";

// Not-yet-loaded permissions count as "not a viewer", so nothing flashes on load.
export const isCompanyViewer = (featurePermissions?: FeaturePermissions[]) =>
  !!featurePermissions &&
  !isPermitted(featurePermissions, COMPANY_VIEWER_PROXY_PERMISSION);

const { wrapper, title } = typeClassNames.warning;

const CompanyViewerNotice: FC = () => (
  <div
    className={`flex items-start gap-2 rounded-md border border-yellow-200 p-3 text-sm ${wrapper} ${title}`}
  >
    <Info className={`mt-0.5 shrink-0 ${SMALL_ICON_SIZE_STYLES}`} />
    <span>
      Your account doesn&apos;t have access to this company&apos;s data yet.
      Wait for a confirmation by your company owner, or contact{" "}
      <a
        className="underline hover:no-underline"
        href="mailto:support@vviinn.com"
      >
        support@vviinn.com
      </a>
      . If your access has already been confirmed, sign out and sign in again to
      apply it.
    </span>
  </div>
);

// Company viewers get only the notice, no page content, on every route below it.
export const CompanyViewerGate: FC = () => {
  const { pages, status: pagesStatus } = useAppSelector((state) => state.pages);
  const companyFailed = useAppSelector(
    (state) => state.company.status === "error",
  );
  const companyId = useAppSelector((state) => state.company.company?.id);
  const permissions = useAppSelector(
    (state) => state.company.company?.featurePermissions,
  );
  const urlCompanyId = useMatch("/company/:companyId/*")?.params.companyId;
  if (pagesStatus === "loading") return <Skeleton />;
  // Global roles are known from pages, which loads before company,
  // so they skip the wait for company permissions.
  if (isGlobalRole(pages)) return <Outlet />;
  // A failed company load falls through to the page, as before the gate.
  if (permissions === undefined)
    return companyFailed ? <Outlet /> : <Skeleton />;
  // While switching companies the URL catches up with the store (useCompanySelect);
  // don't decide on one company's permissions for another company's pages.
  if (urlCompanyId && urlCompanyId !== companyId) return <Skeleton />;
  return isCompanyViewer(permissions) ? <CompanyViewerNotice /> : <Outlet />;
};
